// Terra gate — per-recipient credentials + access tracking.
//
// The Terra page is gated and sent to named recipients. Instead of one shared
// password, each recipient gets their own (email, password) pair so we can tell
// *who* opened the page, and we log the IP + device of every unlock so a
// forwarded password shows up as one credential opening from multiple
// locations/machines.
//
// Validation and logging are client-side against Firestore (the site is a
// static, serverless deploy). This is the right tool for "detect a forwarded
// password among trusted recipients" — it is not a hardened boundary against
// someone reverse-engineering the bundle to skip logging. See firestore.rules
// for the read/write posture.

import {
  doc,
  getDoc,
  addDoc,
  collection,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';

export const TERRA_CREDENTIALS = 'terraCredentials';
export const TERRA_ACCESS_LOGS = 'terraAccessLogs';

// Internal accounts whose Terra-gate activity should never appear in the access
// data — the operator testing the page. These are skipped at write time and
// filtered out of the admin views (so any already-recorded rows disappear too).
export const EXCLUDED_EMAILS = ['isaaclongdev@gmail.com'];

export function isExcludedEmail(email: string | undefined): boolean {
  return EXCLUDED_EMAILS.includes((email || '').trim().toLowerCase());
}

/**
 * Doc id for a credential = SHA-256("email:password") in lowercase hex.
 * The id is the proof of knowledge — a viewer who can compute it already knows
 * the credentials, and cannot derive any *other* credential's id from it.
 */
export async function hashCredential(email: string, password: string): Promise<string> {
  const normalized = `${email.trim().toLowerCase()}:${password}`;
  const bytes = new TextEncoder().encode(normalized);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export interface CredentialResult {
  ok: boolean;
  id: string;
  email?: string;
  label?: string;
}

/** Validate an (email, password) pair against the terraCredentials collection. */
export async function validateCredential(
  email: string,
  password: string,
): Promise<CredentialResult> {
  const id = await hashCredential(email, password);
  try {
    const snap = await getDoc(doc(db, TERRA_CREDENTIALS, id));
    if (!snap.exists()) return { ok: false, id };
    const data = snap.data();
    if (data.active === false) return { ok: false, id };
    return { ok: true, id, email: data.email, label: data.label };
  } catch {
    return { ok: false, id };
  }
}

export interface DeviceInfo {
  userAgent: string;
  browser: string;
  os: string;
  deviceType: string;
  language: string;
  timezone: string;
  screen: string;
}

/** Coarse user-agent parse — enough to tell devices apart in the admin view. */
export function parseDevice(ua: string): { browser: string; os: string; deviceType: string } {
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /OPR\/|Opera/.test(ua)
      ? 'Opera'
      : /Chrome\//.test(ua)
        ? 'Chrome'
        : /Firefox\//.test(ua)
          ? 'Firefox'
          : /Safari\//.test(ua)
            ? 'Safari'
            : 'Unknown';

  const os = /Windows/.test(ua)
    ? 'Windows'
    : /(iPhone|iPad|iPod)/.test(ua)
      ? 'iOS'
      : /Mac OS X|Macintosh/.test(ua)
        ? 'macOS'
        : /Android/.test(ua)
          ? 'Android'
          : /Linux/.test(ua)
            ? 'Linux'
            : 'Unknown';

  const deviceType = /iPad|Tablet/.test(ua)
    ? 'Tablet'
    : /Mobi|Android|iPhone|iPod/.test(ua)
      ? 'Mobile'
      : 'Desktop';

  return { browser, os, deviceType };
}

/** Snapshot of the current browser/device for fingerprinting an access. */
export function collectDeviceInfo(): DeviceInfo {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const { browser, os, deviceType } = parseDevice(ua);
  let timezone = '';
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    /* ignore */
  }
  return {
    userAgent: ua,
    browser,
    os,
    deviceType,
    language: typeof navigator !== 'undefined' ? navigator.language || '' : '',
    timezone,
    screen:
      typeof window !== 'undefined' && window.screen
        ? `${window.screen.width}x${window.screen.height}`
        : '',
  };
}

/** Best-effort public IP via ipify. Returns 'unknown' on any failure. */
export async function getClientIp(): Promise<string> {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 6000);
    const res = await fetch('https://api.ipify.org?format=json', {
      signal: controller.signal,
    });
    clearTimeout(timer);
    if (!res.ok) return 'unknown';
    const data = (await res.json()) as { ip?: unknown };
    return typeof data.ip === 'string' ? data.ip : 'unknown';
  } catch {
    return 'unknown';
  }
}

export type AccessEvent = 'unlock' | 'download';

export interface AccessLogEntry {
  email: string;
  label: string;
  credentialId: string;
  success: boolean;
  event: AccessEvent;
  ip: string;
  referrer: string;
  device: DeviceInfo;
}

/** Append an access record. Non-fatal: a logging failure never blocks unlock. */
export async function logAccess(entry: AccessLogEntry): Promise<void> {
  try {
    await addDoc(collection(db, TERRA_ACCESS_LOGS), {
      email: entry.email,
      label: entry.label,
      credentialId: entry.credentialId,
      success: entry.success,
      event: entry.event,
      ip: entry.ip,
      referrer: entry.referrer,
      userAgent: entry.device.userAgent,
      browser: entry.device.browser,
      os: entry.device.os,
      deviceType: entry.device.deviceType,
      language: entry.device.language,
      timezone: entry.device.timezone,
      screen: entry.device.screen,
      createdAt: serverTimestamp(),
    });
  } catch {
    /* logging is best-effort */
  }
}

/**
 * Capture a full access record (IP + device) and write it. Used by the gate
 * for both successful unlocks and failed attempts.
 */
export async function recordAccess(params: {
  email: string;
  label: string;
  credentialId: string;
  success: boolean;
  event?: AccessEvent;
}): Promise<void> {
  // Operator's own testing never enters the access data.
  if (isExcludedEmail(params.email)) return;
  const ip = await getClientIp();
  await logAccess({
    email: params.email,
    label: params.label,
    credentialId: params.credentialId,
    success: params.success,
    event: params.event ?? 'unlock',
    ip,
    referrer: typeof document !== 'undefined' ? document.referrer || '' : '',
    device: collectDeviceInfo(),
  });
}
